import { useEffect, useState } from "react";
import { fetchVenueDetail, type VenueFact } from "@/api/discover";

const LABELS: Record<VenueFact["fact_type"], string> = {
  booking_url: "Booking form",
  booking_email: "Booking email",
  general_email: "General email",
  phone: "Phone",
  official_website: "Website",
  venue_type: "Venue type",
  capacity: "Capacity",
};
const SECTIONS: { title: string; types: VenueFact["fact_type"][] }[] = [
  { title: "Booking", types: ["booking_url", "booking_email"] },
  { title: "Contact", types: ["general_email", "phone", "official_website"] },
  { title: "Venue info", types: ["venue_type", "capacity"] },
];

function factHref(fact: VenueFact): string | null {
  if (fact.fact_type === "booking_email" || fact.fact_type === "general_email") return `mailto:${fact.value}`;
  if (fact.fact_type === "phone") return `tel:${fact.value}`;
  if (fact.fact_type === "booking_url" || fact.fact_type === "official_website") return fact.value;
  return null;
}

/** Displays the accepted canonical projection; acquisition and CRM remain separate. */
export default function PublicVenueFacts({ facts }: { facts: VenueFact[] }) {
  return (
    <div className="space-y-4">
      {SECTIONS.map(({ title, types }) => {
        const rows = facts.filter((fact) => types.includes(fact.fact_type));
        if (!rows.length) return null;
        return (
          <section key={title} aria-label={title}>
            <h3 className="font-semibold">{title}</h3>
            <ul className="space-y-1">
              {rows.map((fact) => {
                const href = factHref(fact);
                return (
                  <li key={`${fact.fact_type}-${fact.value}`} className="break-words">
                    <span className="text-muted-foreground">{LABELS[fact.fact_type]}: </span>
                    {href ? (
                      <a href={href} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                        {fact.value}
                      </a>
                    ) : fact.value}{" "}
                    {(fact.sources ?? [{ source_url: fact.source_url }]).map((source) => (
                      <a key={source.source_url} href={source.source_url} target="_blank" rel="noreferrer" className="text-xs text-muted-foreground hover:underline">
                        source ↗{" "}
                      </a>
                    ))}
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

/** Legacy detail surfaces may join only through an explicit canonical ID. */
export function CanonicalVenueFacts({ globalVenueId, artistId }: { globalVenueId: string | null; artistId: string | null }) {
  const [facts, setFacts] = useState<VenueFact[]>([]);
  useEffect(() => {
    setFacts([]);
    if (!globalVenueId || !artistId) return;
    let cancelled = false;
    fetchVenueDetail(globalVenueId, artistId)
      .then((detail) => { if (!cancelled) setFacts(detail.facts); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [globalVenueId, artistId]);
  return <PublicVenueFacts facts={facts} />;
}
